import { ActionIcon, Center, Group, Loader, Stack, Text, TextInput } from '@mantine/core'
import { IconArrowLeft, IconBook, IconSearch } from '@tabler/icons-react'
import { type AppId, apps, type GuideKind, guideKind, renderGuideText, sectionsFor } from '@tnn/core'
import { findResource } from '@tnn/domain'
import { useTranslation } from '@tnn/i18n'
import { EmptyState, Page, RecordCard } from '@tnn/ui'
import { useState } from 'react'
import { useParams } from 'react-router'
import { AppMark } from '../app/chrome'
import { useGoBack } from '../app/stack'
import { useAppAbility } from '../auth/session'
import { ResourceAccess } from './access'
import { GuideText, ResourceFacts, useGuides, useGuideTitle } from './guides'
import classes from './guides.module.css'

/** The app a guide belongs to, for its mark: a shared module's (cash, access) is Thananan OS's */
const appOf = (key: string) => {
  const home = key.split('.')[0] ?? ''
  return (home in apps ? home : 'portal') as AppId
}

/** The order the guides page lists them in: what to do when something happens, how things work, then each page */
const kinds: readonly GuideKind[] = ['task', 'topic', 'page']

/** Thananan OS's guides (`/guides`): every guide by its kind, found by any word of it */
export function GuidesPage() {
  const { t } = useTranslation()
  const ability = useAppAbility()
  const { data: guides } = useGuides()
  const title = useGuideTitle()
  const [query, setQuery] = useState('')
  const wanted = query.trim().toLowerCase()
  const text = (raw: string, key: string) =>
    renderGuideText(raw, { resource: findResource(key), find: findResource, guides, t: (k) => t(k) })
  const found = guides?.filter(
    (g) =>
      !wanted ||
      [title(g), g.summary, ...sectionsFor(g, ability).flatMap((s) => [s.title, s.body])].some((part) =>
        text(part, g.key).toLowerCase().includes(wanted),
      ),
  )
  return (
    <Page
      title={t('guides.title')}
      surface="canvas"
      toolbar={
        <TextInput
          leftSection={<IconSearch size={18} />}
          placeholder={t('guides.search')}
          aria-label={t('guides.search')}
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          maw={420}
        />
      }
    >
      {!found ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : found.length === 0 ? (
        <EmptyState icon={IconBook} title={t('guides.none')} />
      ) : (
        <Stack gap="lg" className={classes.wrap}>
          {kinds.map((kind) => {
            const listed = found.filter((g) => guideKind(g, findResource) === kind)
            return (
              listed.length > 0 && (
                <Stack key={kind} gap="sm">
                  <Text size="xs" c="dimmed" fw={500}>
                    {t(`guides.kinds.${kind}`)}
                  </Text>
                  {listed.map((g) => (
                    <RecordCard
                      key={g.key}
                      href={`/guides/${g.key}`}
                      title={title(g)}
                      subtitle={text(g.summary, g.key)}
                      avatar={<AppMark id={appOf(g.key)} size={36} />}
                    />
                  ))}
                </Stack>
              )
            )
          })}
        </Stack>
      )}
    </Page>
  )
}

/** One guide (`/guides/:key`): what its sections tell the reader, and what the registry knows of its resource */
export function GuidePage() {
  const { t } = useTranslation()
  const goBack = useGoBack()
  const { key } = useParams()
  const { data: guides } = useGuides()
  const title = useGuideTitle()
  const guide = guides?.find((g) => g.key === key)
  const resource = key ? findResource(key) : undefined
  const back = (
    <ActionIcon size={40} onClick={() => goBack('/guides')} aria-label={t('actions.back')} ml={-10}>
      <IconArrowLeft size={22} stroke={1.75} />
    </ActionIcon>
  )
  return (
    <Page
      title={
        <Group gap={4} wrap="nowrap" component="span">
          {back}
          <span>{guide ? title(guide) : t('guides.title')}</span>
        </Group>
      }
    >
      {!guides ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : !guide ? (
        <EmptyState icon={IconBook} title={t('guides.none')} />
      ) : (
        <Stack gap="xl" className={`${classes.wrap} ${classes.article}`}>
          <GuideText guide={guide} guides={guides} />
          {resource && <ResourceFacts resource={resource} />}
          {resource && <ResourceAccess resource={resource} />}
        </Stack>
      )}
    </Page>
  )
}
